import React, { useEffect, useMemo, useState } from 'react';
import { MdAdd, MdClose, MdEdit, MdSearch } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import {
  ClearFiltersButton,
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
  TableBodySkeleton,
} from '@euroasiann/shared/ui';

type SurveyorStatus = 'active' | 'inactive';

interface SocietyOption {
  _id: string;
  name: string;
  shortCode: string;
  status: 'active' | 'inactive';
}

interface SurveyorRow {
  _id: string;
  societyId: string;
  societyName: string;
  societyShortCode: string;
  surveyorName: string;
  surveyorEmail: string;
  phone?: string;
  status: SurveyorStatus;
}

interface SurveyorFormData {
  societyId: string;
  surveyorName: string;
  surveyorEmail: string;
  phone: string;
  status: SurveyorStatus;
}

const INITIAL_FORM: SurveyorFormData = {
  societyId: '',
  surveyorName: '',
  surveyorEmail: '',
  phone: '',
  status: 'active',
};

function parseListPayload(payload: any): any[] {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function resolveSocietyLabel(society: SocietyOption): string {
  return society.shortCode ? `${society.shortCode} - ${society.name}` : society.name;
}

export function ClassificationAllSurveyorsPage() {
  const { showToast } = useToast();

  const [surveyors, setSurveyors] = useState<SurveyorRow[]>([]);
  const [societies, setSocieties] = useState<SocietyOption[]>([]);
  const [statusTab, setStatusTab] = useState<SurveyorStatus>('active');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSurveyor, setEditingSurveyor] = useState<SurveyorRow | null>(null);
  const [formData, setFormData] = useState<SurveyorFormData>(INITIAL_FORM);
  const [searchQuery, setSearchQuery] = useState('');
  const [societyFilterId, setSocietyFilterId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const filteredSurveyors = useMemo(() => {
    let rows = surveyors;
    if (societyFilterId) {
      rows = rows.filter(row => row.societyId === societyFilterId);
    }
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(row => {
      const haystack = [
        row.surveyorName,
        row.surveyorEmail,
        row.phone || '',
        row.societyName,
        row.societyShortCode,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [surveyors, searchQuery, societyFilterId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusTab, societyFilterId]);

  const totalPages = Math.max(1, Math.ceil(filteredSurveyors.length / PAGE_SIZE));
  const paginatedSurveyors = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSurveyors.slice(start, start + PAGE_SIZE);
  }, [filteredSurveyors, currentPage]);

  const fetchSocieties = async (): Promise<SocietyOption[]> => {
    const response = await authenticatedFetch('/api/v1/admin/classification-societies?limit=500');
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch classification societies');
    }
    const payload = await response.json();
    return parseListPayload(payload)
      .map((item: any) => ({
        _id: String(item._id || ''),
        name: String(item.name || ''),
        shortCode: String(item.shortCode || ''),
        status: item.status === 'inactive' ? 'inactive' : 'active',
      }))
      .filter((item: SocietyOption) => item._id && item.name);
  };

  const fetchSurveyors = async () => {
    setLoading(true);
    try {
      const societyList = await fetchSocieties();
      setSocieties(societyList);

      const batches = await Promise.all(
        societyList.map(async society => {
          const response = await authenticatedFetch(
            `/api/v1/admin/classification-societies/${society._id}/surveyors?status=${statusTab}&limit=500`
          );
          if (!response.ok) return [] as SurveyorRow[];
          const payload = await response.json();
          return parseListPayload(payload).map((surveyor: any) => ({
            _id: String(surveyor._id || ''),
            societyId: society._id,
            societyName: society.name,
            societyShortCode: society.shortCode,
            surveyorName: String(surveyor.surveyorName || ''),
            surveyorEmail: String(surveyor.surveyorEmail || ''),
            phone: surveyor.phone ? String(surveyor.phone) : undefined,
            status: surveyor.status === 'inactive' ? 'inactive' : 'active',
          }));
        })
      );

      const rows = batches
        .flat()
        .filter(row => row._id)
        .sort((a, b) => {
          const societyCmp = a.societyShortCode.localeCompare(b.societyShortCode);
          if (societyCmp !== 0) return societyCmp;
          return a.surveyorName.localeCompare(b.surveyorName);
        });

      setSurveyors(rows);
    } catch (error: any) {
      showToast(error.message || 'Failed to fetch surveyors', 'error');
      setSurveyors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSurveyors();
  }, [statusTab]);

  const openCreateModal = () => {
    setEditingSurveyor(null);
    setFormData(INITIAL_FORM);
    setIsModalOpen(true);
  };

  const openEditModal = (surveyor: SurveyorRow) => {
    setEditingSurveyor(surveyor);
    setFormData({
      societyId: surveyor.societyId,
      surveyorName: surveyor.surveyorName,
      surveyorEmail: surveyor.surveyorEmail,
      phone: surveyor.phone || '',
      status: surveyor.status,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSurveyor(null);
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async () => {
    if (!formData.surveyorName.trim() || !formData.surveyorEmail.trim()) {
      showToast('Surveyor name and email are required', 'error');
      return;
    }
    if (!editingSurveyor && !formData.societyId) {
      showToast('Classification society is required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = editingSurveyor
        ? `/api/v1/admin/classification-surveyors/${editingSurveyor._id}`
        : `/api/v1/admin/classification-societies/${formData.societyId}/surveyors`;
      const method = editingSurveyor ? 'PUT' : 'POST';

      const response = await authenticatedFetch(endpoint, {
        method,
        body: JSON.stringify({
          surveyorName: formData.surveyorName,
          surveyorEmail: formData.surveyorEmail,
          phone: formData.phone,
          status: formData.status,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save surveyor');
      }

      showToast(
        editingSurveyor ? 'Surveyor updated successfully' : 'Surveyor created successfully',
        'success'
      );
      closeModal();
      await fetchSurveyors();
    } catch (error: any) {
      showToast(error.message || 'Failed to save surveyor', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (surveyor: SurveyorRow) => {
    if (!window.confirm(`Set surveyor "${surveyor.surveyorName}" to inactive?`)) return;
    try {
      const response = await authenticatedFetch(
        `/api/v1/admin/classification-surveyors/${surveyor._id}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to deactivate surveyor');
      }
      showToast('Surveyor set to inactive', 'success');
      await fetchSurveyors();
    } catch (error: any) {
      showToast(error.message || 'Failed to deactivate surveyor', 'error');
    }
  };

  const activeSocieties = societies.filter(society => society.status === 'active');
  const societyFilterOptions = [...societies].sort((a, b) =>
    a.shortCode.localeCompare(b.shortCode)
  );

  return (
    <div className="w-full min-h-screen p-8">
      <div className="mb-6">
        <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
          <span className={erpPageBreadcrumbItemClass}>Admin</span>
          <span className={erpPageBreadcrumbSepClass}>/</span>
          <span className={erpPageBreadcrumbCurrentClass}>Classification</span>
        </nav>
        <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">Classification Surveyors</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          All surveyors across classification societies.
        </p>
      </div>

      <div className="mb-6 flex gap-2 border-b border-[hsl(var(--border))]">
        <button
          onClick={() => setStatusTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusTab === 'active'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setStatusTab('inactive')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusTab === 'inactive'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Inactive
        </button>
      </div>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-1 sm:max-w-3xl">
          <select
            value={societyFilterId}
            onChange={e => setSocietyFilterId(e.target.value)}
            className="w-full sm:w-56 shrink-0 px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
            aria-label="Filter by classification society"
          >
            <option value="">All societies</option>
            {societyFilterOptions.map(society => (
              <option key={society._id} value={society._id}>
                {resolveSocietyLabel(society)}
              </option>
            ))}
          </select>
          <div className="relative flex-1 min-w-0">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0088cc]/65 w-5 h-5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by surveyor, email, phone, or society..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
            />
          </div>
          <ClearFiltersButton
            visible={!!searchQuery.trim() || !!societyFilterId}
            onClick={() => {
              setSearchQuery('');
              setSocietyFilterId('');
            }}
            variant="toolbar"
          />
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex shrink-0 items-center justify-center gap-2 px-4 py-2.5 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
        >
          <MdAdd className="w-5 h-5" />
          Add Surveyor
        </button>
      </div>

      <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[hsl(var(--secondary))]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                  Society
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[hsl(var(--card))] divide-y divide-[#0088cc]/20 dark:divide-gray-800">
              {loading ? (
                <TableBodySkeleton rows={6} cols={6} />
              ) : filteredSurveyors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-[hsl(var(--muted-foreground))]">
                    {searchQuery.trim() || societyFilterId
                      ? 'No surveyors match your search or filter.'
                      : 'No surveyors found.'}
                  </td>
                </tr>
              ) : (
                paginatedSurveyors.map(surveyor => (
                  <tr key={surveyor._id} className="hover:bg-[hsl(var(--secondary))]/40">
                    <td className="px-6 py-4 text-sm font-semibold text-[hsl(var(--foreground))]">
                      {surveyor.societyShortCode}
                      <span className="block text-xs font-normal text-[hsl(var(--muted-foreground))]">
                        {surveyor.societyName}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">{surveyor.surveyorName}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">{surveyor.surveyorEmail}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">{surveyor.phone || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          surveyor.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}
                      >
                        {surveyor.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(surveyor)}
                          className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400"
                          title="Edit"
                        >
                          <MdEdit className="w-5 h-5" />
                        </button>
                        {surveyor.status === 'active' && (
                          <button
                            onClick={() => handleDeactivate(surveyor)}
                            className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                            title="Deactivate"
                          >
                            <MdClose className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredSurveyors.length > 0 && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Page {currentPage} of {totalPages} · {filteredSurveyors.length} total
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--secondary))] transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--secondary))] transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-[hsl(var(--card))] rounded-lg shadow-xl w-full max-w-xl mx-4 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))]">
              <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">
                {editingSurveyor ? 'Edit Surveyor' : 'Add Surveyor'}
              </h2>
              <button
                onClick={closeModal}
                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] dark:hover:text-gray-200"
              >
                <MdClose className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Classification Society</label>
                <select
                  value={formData.societyId}
                  onChange={e => setFormData(prev => ({ ...prev, societyId: e.target.value }))}
                  disabled={Boolean(editingSurveyor)}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))] disabled:opacity-60"
                >
                  <option value="">Select society</option>
                  {(editingSurveyor ? societies : activeSocieties).map(society => (
                    <option key={society._id} value={society._id}>
                      {resolveSocietyLabel(society)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Surveyor Name</label>
                <input
                  type="text"
                  value={formData.surveyorName}
                  onChange={e => setFormData(prev => ({ ...prev, surveyorName: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Surveyor Email</label>
                <input
                  type="email"
                  value={formData.surveyorEmail}
                  onChange={e => setFormData(prev => ({ ...prev, surveyorEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone (Optional)</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              {editingSurveyor && (
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, status: e.target.value as SurveyorStatus }))
                    }
                    className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                  >
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[hsl(var(--border))]">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-[#0088cc] text-[hsl(var(--primary))] rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : editingSurveyor ? 'Update Surveyor' : 'Add Surveyor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
